import { useState, useEffect } from "react";
import { TrendingUp, Clock, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function WhyNowSection() {
  const [investorCount, setInvestorCount] = useState(23);

  useEffect(() => {
    // Initialize counter from localStorage
    const storedCount = localStorage.getItem('investorCountThisWeek');
    if (storedCount) {
      setInvestorCount(parseInt(storedCount));
    } else {
      localStorage.setItem('investorCountThisWeek', '23');
    }

    // Listen for form submission events
    const handleFormSubmit = () => {
      const currentCount = parseInt(localStorage.getItem('investorCountThisWeek') || '23');
      const newCount = currentCount + 1;
      localStorage.setItem('investorCountThisWeek', newCount.toString());
      setInvestorCount(newCount);
    };

    window.addEventListener('investorFormSubmitted', handleFormSubmit);

    return () => {
      window.removeEventListener('investorFormSubmitted', handleFormSubmit);
    };
  }, []);

  const scrollToForm = () => {
    const formElement = document.getElementById('contact-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const reasons = [
    {
      icon: TrendingUp,
      title: "Pre-Launch Prices for High ROI",
      highlight: "Save Up to 25%",
      description: "Secure your international property investment in Dubai at pre-launch prices. This is a key strategy for achieving high ROI in the Dubai property market. Prices for these Dubai Motor City apartments are set to rise.",
      urgency: "Only 47 units left at current pricing",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Clock,
      title: "Exclusive Dubai Property Payment Plan",
      highlight: "20% Down Only",
      description: "This limited-time Dubai property payment plan makes your Dubai residential investment more accessible. It's the perfect plan for those looking to invest in Dubai from Turkey, India, or the UK.",
      urgency: "Payment plan expires in 45 days",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Award,
      title: "Secure the Best Rental Yield in Dubai",
      highlight: "10%+ Income Target",
      description: "As an early investor, you get priority tenant placement, helping you achieve the best rental yield in Dubai. This is how you secure 10% rental income in Dubai from day one.",
      urgency: "First 100 investors only",
      color: "from-amber-500 to-orange-600"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-4 animate-pulse">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-bold">TIME-SENSITIVE OPPORTUNITY</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Dubai Real Estate 2024: Your Prime
            <span className="block text-primary mt-2">Investment Opportunity</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Seize this Dubai investment opportunity. Early investors in Motor City Dubai property secure exclusive benefits for high ROI.
          </p>
        </div>

        {/* 3 Reasons - Carousel on Mobile, Grid on Desktop */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 mb-12">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <div
                key={index}
                className="group relative bg-card border-2 border-border hover:border-primary rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
              >
                {/* Number Badge */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                  {index + 1}
                </div>

                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-2">{reason.title}</h3>
                <div className={`text-2xl font-bold bg-gradient-to-r ${reason.color} bg-clip-text text-transparent mb-4`}>
                  {reason.highlight}
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {reason.description}
                </p>

                {/* Urgency Badge */}
                <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-3 py-1.5 rounded-full text-sm font-semibold">
                  <span className="w-2 h-2 bg-destructive rounded-full animate-pulse"></span>
                  {reason.urgency}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden mb-12">
          <div className="relative overflow-hidden">
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-4 -mx-4">
              {reasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <div
                    key={index}
                    className="flex-none w-[85vw] snap-center"
                  >
                    <div className="group relative bg-card border-2 border-border hover:border-primary rounded-2xl p-8 h-full">
                      {/* Number Badge */}
                      <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                        {index + 1}
                      </div>

                      {/* Icon */}
                      <div className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-xl flex items-center justify-center mb-6`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl font-bold mb-2">{reason.title}</h3>
                      <div className={`text-2xl font-bold bg-gradient-to-r ${reason.color} bg-clip-text text-transparent mb-4`}>
                        {reason.highlight}
                      </div>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {reason.description}
                      </p>

                      {/* Urgency Badge */}
                      <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-3 py-1.5 rounded-full text-sm font-semibold">
                        <span className="w-2 h-2 bg-destructive rounded-full animate-pulse"></span>
                        {reason.urgency}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Carousel Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {reasons.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-muted-foreground/30"
              />
            ))}
          </div>
        </div>

        {/* CTA Box */}
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-2xl p-10 text-center shadow-2xl">
          <h3 className="text-3xl font-bold mb-4">
            Your Dubai Property for Sale is Waiting
          </h3>
          <p className="text-lg mb-6 opacity-90">
            This is a premier Dubai investment opportunity. Join over 500 international investors who have secured their future. Act now for maximum returns on your Dubai apartment investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              variant="secondary"
              className="text-lg px-8 py-6 font-bold shadow-xl hover:scale-105 transition-transform"
              onClick={scrollToForm}
            >
              Reserve My Unit Now
            </Button>
            <div className="text-sm opacity-80">
              ⚡ Average response time: 2 hours
            </div>
          </div>
        </div>

        {/* Social Proof Strip */}
        <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-center">
          <div>
            <div className="text-3xl font-bold text-primary">47</div>
            <div className="text-sm text-muted-foreground">Units Remaining</div>
          </div>
          <div className="hidden sm:block w-px h-12 bg-border"></div>
          <div>
            <div className="text-3xl font-bold text-primary">45</div>
            <div className="text-sm text-muted-foreground">Days Until Price Increase</div>
          </div>
          <div className="hidden sm:block w-px h-12 bg-border"></div>
          <div>
            <div className="text-3xl font-bold text-primary">{investorCount}</div>
            <div className="text-sm text-muted-foreground">Investors This Week</div>
          </div>
        </div>
      </div>
    </section>
  );
}
